import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { OrganisationListItem } from '@assetaccountant/api/models/organisation-list-item';
import { OrganisationV2Api } from '@assetaccountant/api/services/organisation-v2.api';
import { Observable, firstValueFrom, map, shareReplay } from 'rxjs';
import { AppNavigationService } from './app-navigation.service';

/** Handles a client picked in the platform navigation bar (FAM-156). */
@Injectable({ providedIn: 'root' })
export class ClientSwitchService {
  // Cached for the page load so browsing the client list costs nothing.
  private organisations$?: Observable<OrganisationListItem[]>;

  constructor(
    private organisationApi: OrganisationV2Api,
    private appNavigation: AppNavigationService,
    private router: Router,
  ) {}

  /** The organisation behind a ucid, undefined if the user has none. Throws if the lookup fails. */
  async findOrganisation(universalClientId: string) {
    try {
      const organisations = await firstValueFrom(this.getOrganisations());
      return organisations.find(
        org => org.universalClientId?.toLowerCase() === universalClientId.toLowerCase(),
      );
    } catch (error) {
      // Drop the cache so the next try refetches instead of replaying the failure.
      this.organisations$ = undefined;
      throw error;
    }
  }

  /** Switches to the organisation behind a ucid, or shows Not authorized if we hold none. */
  async switchToClient(universalClientId: string | null | undefined) {
    if (!universalClientId) {
      return false;
    }

    let match: OrganisationListItem | undefined;
    try {
      match = await this.findOrganisation(universalClientId);
    } catch {
      // A blip is not an authorization problem, so leave them where they are.
      return false;
    }

    if (!match) {
      await this.router.navigate(['/unauthorized'], {
        queryParams: { returnUrl: this.router.url },
      });
      return false;
    }

    this.appNavigation.switchOrganisation(match);
    return true;
  }

  private getOrganisations() {
    this.organisations$ ??= this.organisationApi.listOrganisations2().pipe(
      map(({ result }) => result ?? []),
      shareReplay(1),
    );

    return this.organisations$;
  }
}
